import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { EFreightLoadingType, EVehicleType } from '@okcargo-apps/shared-domain';
import {
  AcceptFreightAssignationFromMarketCommand,
  AddFreightCollaboratorInterestedCommand,
} from '@okcargo/command-processor';
import {
  CriteriaPayload,
  FilterCombinator,
  FilterComparator,
  FilterPayload,
  FindMarketFreightQuery,
  FreightFieldNames,
  FreightStatus,
  OrderPayload,
  OrderType,
  SearchAllMarketFreightsQuery,
} from '@okcargo/query-processor';
import { isPast } from 'date-fns';
import { BehaviorSubject, from, map, Observable, of, switchMap } from 'rxjs';
import { MarketCommandService } from '../infrastructure/market-command.service';
import { MarketQueryService } from '../infrastructure/market-query.service';
import {
  getEmptyDefaultMarketFreight,
  IAddressMarket,
  IFreightMarketForCarrier,
  IStopMarketForCarrier,
} from './../domain/market.model';

@Injectable({
  providedIn: 'root',
})
export class MarketService {
  private readonly marketQueryService = inject(MarketQueryService);
  private readonly marketCommandService = inject(MarketCommandService);

  private readonly page: number = 0;
  private readonly size: number = 200;

  private readonly _countTotal: WritableSignal<number> = signal(0);
  private readonly _countWithFilters: WritableSignal<number> = signal(0);

  private filters: FilterPayload[] = [];

  private originCountryCode: string | undefined;
  private originStateCode: string | undefined;
  private destinationCountryCode: string | undefined;
  private destinationStateCode: string | undefined;

  private readonly _freightsInMarket$ = new BehaviorSubject<IFreightMarketForCarrier[]>([]);

  private readonly _refreshMarketDetail$ = new BehaviorSubject<void>(undefined);

  public freightsInMarketLoading = signal(true);

  public countTotal = this._countTotal.asReadonly();
  public countWithFilters = this._countWithFilters.asReadonly();

  private readonly _filteredRoutes = [
    '12_28',
    '40_08',
    '40_46',
    '28_33',
    '19_41',
    '28_28',
    '28_19',
    '19_28',
    '19_19',
    '28_45',
    '45_28',
    '19_45',
    '45_19',
  ];

  // constructor() {}

  applyFilter(
    originCountryCode: string | undefined,
    originStateCode: string | undefined,
    destinationCountryCode: string | undefined,
    destinationStateCode: string | undefined,
  ): void {
    this.filters = [];
    this.originCountryCode = originCountryCode;
    this.originStateCode = originStateCode;
    this.destinationCountryCode = destinationCountryCode;
    this.destinationStateCode = destinationStateCode;
    this.addFilters('origin', this.originCountryCode, this.originStateCode);
    this.addFilters('destination', this.destinationCountryCode, this.destinationStateCode);
    this.refreshFreightsInMarket();
  }

  get filter(): any {
    return {
      originCountryCode: this.originCountryCode,
      originStateCode: this.originStateCode,
      destinationCountryCode: this.destinationCountryCode,
      destinationStateCode: this.destinationStateCode,
    } as any;
  }

  private addFilters(type: string, countryCode: string | undefined, stateCode: string | undefined): void {
    if (countryCode) {
      this.filters.push({
        combinator: this.filters.length === 0 ? FilterCombinator.NONE : FilterCombinator.AND,
        field: type === 'origin' ? FreightFieldNames.RAW_FIRST_STOP : FreightFieldNames.RAW_LAST_STOP,
        comparator: FilterComparator.LIKE,
        value: '%"countryCode":"' + countryCode + '"%',
      } as FilterPayload);
    }
    if (stateCode) {
      this.filters.push({
        combinator: this.filters.length === 0 ? FilterCombinator.NONE : FilterCombinator.AND,
        field: type === 'origin' ? FreightFieldNames.RAW_FIRST_STOP : FreightFieldNames.RAW_LAST_STOP,
        comparator: FilterComparator.LIKE,
        value: '%"stateCode":"' + stateCode + '"%',
      } as FilterPayload);
    }
  }

  private isFilterEmpty(): boolean {
    const filterObj = this.filter;
    return Object.values(filterObj).every((value) => value === undefined || value === null);
  }

  get freightsInMarket$(): BehaviorSubject<IFreightMarketForCarrier[]> {
    return this._freightsInMarket$;
  }

  get refreshMarketDetail$(): Observable<void> {
    return this._refreshMarketDetail$.asObservable();
  }

  public refreshFreightsInMarket(): void {
    this.searchFreightsInMarket();
  }

  public refreshMarketDetail(): void {
    this._refreshMarketDetail$.next();
  }

  public saveRoutePreference(): void {
    //this.marketCommandService.
  }

  private searchFreightsInMarket(): void {
    const query = {
      criteriaPayload: {
        order: [
          {
            field: FreightFieldNames.LATEST_STARTING_AT, // TODO review this
            // type: this.filters.length > 0 ? OrderType.DESC : OrderType.ASC,
            type: OrderType.ASC,
          } as OrderPayload,
        ],
        filters:
          this.filters.length > 0
            ? this.filters
            : [
                // TODO remove
                // {
                //   combinator: FilterCombinator.NONE,
                //   field: FreightFieldNames.CARRIER_COLLABORATOR_ASSIGNATION_TYPE,
                //   comparator: FilterComparator.EQ,
                //   value: FreightAssignationType.MARKET,
                // } as FilterPayload,
                // TODO review, CREATED and ACCEPTED because the status not change in backend (possible bug?)
                {
                  // combinator: FilterCombinator.AND,
                  combinator: FilterCombinator.NONE,
                  field: FreightFieldNames.STATUS,
                  // comparator: FilterComparator.EQ,
                  comparator: FilterComparator.IN,
                  value: FreightStatus.CREATED + '|' + FreightStatus.ACCEPTED,
                  // value: FreightStatus.ACCEPTED,
                } as FilterPayload,
                // TODO review, do not include freights whose start date is previous to the current time
                // maybe this filter has more sense in the backend
                {
                  combinator: FilterCombinator.AND,
                  // field: FreightFieldNames.STARTING_AT,
                  field: FreightFieldNames.LATEST_STARTING_AT,
                  // comparator: FilterComparator.EQ,
                  comparator: FilterComparator.GT,
                  value: new Date().toISOString(),
                  // value: FreightStatus.ACCEPTED,
                } as FilterPayload,
              ],
        pageIndex: this.page,
        pageSize: this.size,
      } as unknown as CriteriaPayload,
    } as SearchAllMarketFreightsQuery;

    from(this.marketQueryService.searchAllMarketFreights(query))
      .pipe(
        map((freights) =>
          freights
            .map((marketFreightResponse): IFreightMarketForCarrier => {
              const freight: IFreightMarketForCarrier = {
                id: marketFreightResponse.id,
                // journeyId: marketFreightResponse.journeyId,
                numberOfStops: marketFreightResponse.numberOfStops,
                reference: marketFreightResponse.carrierFreightM2mReference,
                price: marketFreightResponse.price,
                stops: this.transformToIStop(marketFreightResponse.stops),
                // status: transformToEFreightStatus(marketFreightResponse.status, false), // assume that no stop has been started
                // carrier: {
                //   id: marketFreightResponse.carrierId as any,
                //   name: marketFreightResponse.carrierName,
                //   phone: marketFreightResponse.carrierPhone,
                //   taxId: marketFreightResponse.carrierTaxId,
                // },
                deliveryNoteInPaperRequired: false,
                instructions: marketFreightResponse.instructions,
                loadingTypes: marketFreightResponse.loadingTypes.map(
                  (vehicle) => vehicle as unknown as EFreightLoadingType,
                ), // marketFreightResponse.loadingTypes,
                vehicleTypes: marketFreightResponse.vehicleTypes.map((vehicle) => vehicle as unknown as EVehicleType), // marketFreightResponse.vehicleTypes,
                nature: marketFreightResponse.nature,
                // packages: marketFreightResponse.packages,
                weight: marketFreightResponse.weight,
                title: 'Carga ' + marketFreightResponse.carrierFreightM2mReference,
                // assignationId: marketFreightResponse.carrierCollaboratorAssignationId,
                // isAssigned: marketFreightResponse.isAssigned,
                distance: marketFreightResponse.distance,
                available: !marketFreightResponse.draft,
                pricePerKilometer: marketFreightResponse.distance
                  ? Math.round((marketFreightResponse.price / marketFreightResponse.distance) * 100) / 100
                  : 0,
              };
              this.excludePriceIfNeeded(freight);
              this.markAsAssignedIfDateIsEarlier(freight); // temporary fix to not assign expired freights when the user locates a freight using the search by states
              return freight; // this.transformToIMarketFreight(marketFreightResponse, freight);
            })
            .sort((a, b) => {
              return a.available === b.available ? 0 : a.available ? 1 : -1;
            }),
        ),
      )
      .subscribe((transformedFreights) => {
        this._freightsInMarket$.next(transformedFreights);
        this.freightsInMarketLoading.set(false);

        this._countWithFilters.set(transformedFreights.length);
        if (this.isFilterEmpty()) {
          this._countTotal.set(transformedFreights.length);
        }
      });
  }

  public excludePriceIfNeeded(freight: IFreightMarketForCarrier): void {
    const originState = freight.stops[0].address.stateCode;
    const destinationState = freight.stops[freight.numberOfStops - 1].address.stateCode;
    if (this._filteredRoutes.includes(originState + '_' + destinationState)) {
      freight.available = false;
      freight.price = 0;
      freight.pricePerKilometer = 0;
    }
  }

  // temporary fix to not assign expired freights when the user locates a freight using the search by states
  public markAsAssignedIfDateIsEarlier(freight: IFreightMarketForCarrier): void {
    if (isPast(freight.stops[0].until)) {
      // freight.status = EFreightStatus.FINISHED;
      freight.available = false;
    }
  }

  public findMarketFreightDetail$(id: string): Observable<IFreightMarketForCarrier> {
    const query = {
      id: id,
    } as FindMarketFreightQuery;

    return from(this.marketQueryService.findFreightFromMarket(query)).pipe(
      switchMap((marketFreightDetailedResponse): Observable<IFreightMarketForCarrier> => {
        if (!marketFreightDetailedResponse || typeof marketFreightDetailedResponse === 'string') {
          // console.warn('Freight vacío');
          const freight = getEmptyDefaultMarketFreight();
          // freight.status = EFreightStatus.DEFAULT;
          return of(freight);
        }

        console.log('MarketDetailStatus ', marketFreightDetailedResponse.status);
        const transformedFreight: IFreightMarketForCarrier = {
          id: marketFreightDetailedResponse.id,
          numberOfStops: marketFreightDetailedResponse.numberOfStops,
          reference: marketFreightDetailedResponse.carrierFreightM2mReference,
          price: marketFreightDetailedResponse.price,
          stops: this.transformToIStop(marketFreightDetailedResponse.stops),
          // status: transformToEFreightStatus(marketFreightDetailedResponse.status, false), // assume that no stop has been started
          // carrier: {
          //   id: marketFreightDetailedResponse.carrierId as any,
          //   name: marketFreightDetailedResponse.carrierName,
          //   phone: marketFreightDetailedResponse.carrierPhone,
          //   taxId: marketFreightDetailedResponse.carrierTaxId,
          // },
          deliveryNoteInPaperRequired: false,
          instructions: marketFreightDetailedResponse.instructions,
          loadingTypes: marketFreightDetailedResponse.loadingTypes.map(
            (vehicle) => vehicle as unknown as EFreightLoadingType,
          ), // marketFreightResponse.loadingTypes,
          nature: marketFreightDetailedResponse.nature,
          // packages: marketFreightDetailedResponse.packages,
          vehicleTypes: marketFreightDetailedResponse.vehicleTypes.map((vehicle) => vehicle as unknown as EVehicleType), // marketFreightResponse.vehicleTypes,
          weight: marketFreightDetailedResponse.weight,
          title: marketFreightDetailedResponse.carrierFreightM2mReference,
          // assignationId: marketFreightDetailedResponse.carrierCollaboratorAssignationId,
          // isAssigned: marketFreightDetailedResponse.isAssigned,
          // journeyId: marketFreightDetailedResponse.journeyId,
          distance: marketFreightDetailedResponse.distance,
          available: !marketFreightDetailedResponse.draft,
          pricePerKilometer: marketFreightDetailedResponse.distance
            ? Math.round((marketFreightDetailedResponse.price / marketFreightDetailedResponse.distance) * 100) / 100
            : 0,
        };
        this.excludePriceIfNeeded(transformedFreight);
        this.markAsAssignedIfDateIsEarlier(transformedFreight); // temporary fix to not assign expired freights when the user locates a freight using the search by states

        // console.log('MarketDetailStatus2 ', transformedFreight.status);

        //return freight; // this.transformToIMarketFreight(marketFreightResponse, freight);
        return of(transformedFreight);
      }),
    );
  }

  public async acceptFreightAssignationFromMarket(id: string): Promise<void> {
    this.marketCommandService
      .acceptFreightAssignationFromMarket({
        // assignationId: assignationId,
        parentAssignationId: null as unknown as string,
        freightId: id,
      } as AcceptFreightAssignationFromMarketCommand)
      .then(
        () => {
          // TODO review this
        },
        async (error: any) => {
          // TODO implement this
          console.warn(error);
        },
      );
  }

  public async showInterestOnFreight(_id: string): Promise<void> {
    this.marketCommandService
      .addFreightCollaboratorInterested({
        id: _id,
        comments: '',
      } as AddFreightCollaboratorInterestedCommand)
      .then(
        () => {
          // TODO review this
        },
        async (error: any) => {
          // TODO implement this
          console.warn(error);
        },
      );
  }

  // TODO mapper, move
  private transformToIStop(stops: any[]): IStopMarketForCarrier[] {
    return stops.map((stop: any) => {
      return {
        id: stop.id,
        address: {
          city: stop.city,
          postalCode: stop.postalCode,
          state: stop.state,
          stateCode: stop.stateCode,
          country: stop.country,
          countryCode: stop.countryCode,
        } as IAddressMarket,
        position: +stop.position, // number
        since: new Date(stop.since),
        until: new Date(stop.until),
        status: stop.status,
        type: stop.type,
      } as IStopMarketForCarrier;
    });
  }
}
